import React from 'react';
import { ArrowRight, Play, CheckCircle } from 'lucide-react';

const Hero: React.FC = () => {
  return (
    <section className="hero-gradient text-white py-20 lg:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Column - Content */}
          <div className="space-y-8">
            <div className="space-y-4">
              <h1 className="text-4xl lg:text-6xl font-bold leading-tight">
                Proactive AWS Security
                <span className="block text-cyan-300">That Prevents Incidents</span>
              </h1>
              <p className="text-xl lg:text-2xl text-blue-100 leading-relaxed">
                Transform your reactive security operations into intelligent, automated protection 
                with AI-powered monitoring and remediation.
              </p>
            </div>

            {/* Key Benefits */}
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <CheckCircle className="h-6 w-6 text-green-400 flex-shrink-0" />
                <span className="text-lg">Continuous proactive monitoring vs. periodic audits</span>
              </div>
              <div className="flex items-center space-x-3">
                <CheckCircle className="h-6 w-6 text-green-400 flex-shrink-0" />
                <span className="text-lg">AI-powered remediation with risk-based prioritization</span>
              </div>
              <div className="flex items-center space-x-3">
                <CheckCircle className="h-6 w-6 text-green-400 flex-shrink-0" />
                <span className="text-lg">Native AWS integration with 40+ security services</span>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <button className="bg-white text-primary-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-50 transition-colors flex items-center justify-center space-x-2">
                <span>Start Free Assessment</span>
                <ArrowRight className="h-5 w-5" />
              </button>
              <button className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-primary-600 transition-colors flex items-center justify-center space-x-2">
                <Play className="h-5 w-5" />
                <span>Watch Demo</span>
              </button>
            </div>

            {/* Trust Indicators */}
            <div className="pt-8">
              <p className="text-blue-200 text-sm mb-4">Powered by Brain Station 23 - Trusted by:</p>
              <div className="flex flex-wrap items-center gap-4 opacity-70">
                <div className="bg-white bg-opacity-20 px-4 py-2 rounded text-sm font-medium">
                  Mid-Market Enterprises
                </div>
                <div className="bg-white bg-opacity-20 px-4 py-2 rounded text-sm font-medium">
                  Digital Startups
                </div>
                <div className="bg-white bg-opacity-20 px-4 py-2 rounded text-sm font-medium">
                  Large Enterprises
                </div>
                <div className="bg-orange-500 bg-opacity-90 px-4 py-2 rounded text-sm font-medium text-white">
                  15+ Years Experience
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Visual */}
          <div className="relative">
            <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-2xl p-8 border border-white border-opacity-20">
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold">Security Dashboard</h3>
                  <div className="flex space-x-2">
                    <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                    <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                    <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="bg-white bg-opacity-20 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm">Security Score</span>
                      <span className="text-2xl font-bold text-green-400">94%</span>
                    </div>
                    <div className="w-full bg-white bg-opacity-20 rounded-full h-2">
                      <div className="bg-green-400 h-2 rounded-full" style={{width: '94%'}}></div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white bg-opacity-20 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-green-400">0</div>
                      <div className="text-sm">Critical Issues</div>
                    </div>
                    <div className="bg-white bg-opacity-20 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-yellow-400">3</div>
                      <div className="text-sm">Recommendations</div>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center justify-between bg-white bg-opacity-20 rounded-lg p-3">
                      <span className="text-sm">CloudTrail Enabled</span>
                      <CheckCircle className="h-5 w-5 text-green-400" />
                    </div>
                    <div className="flex items-center justify-between bg-white bg-opacity-20 rounded-lg p-3">
                      <span className="text-sm">S3 Encryption Active</span>
                      <CheckCircle className="h-5 w-5 text-green-400" />
                    </div>
                    <div className="flex items-center justify-between bg-white bg-opacity-20 rounded-lg p-3">
                      <span className="text-sm">IAM Policies Optimized</span>
                      <CheckCircle className="h-5 w-5 text-green-400" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
